<template>
  <div class="homeEvents">
    <v-sheet v-if="alertEvents.length" class="homeEvents_notification">
      <div class="homeEvents_notification_supTitle">SECOMからの通信</div>
      <div class="homeEvents_notification_title">
        SECOM対応員が現場へ到着しました
      </div>
      <EventItem
        v-for="alertEvent in alertEvents"
        :key="alertEvent.id"
        :event="alertEvent"
        hide-link
      />
    </v-sheet>
    <v-sheet class="homeEvents_main">
      <div class="homeEvents_title">
        <div class="homeEvents_title_text">イベント履歴</div>
        <v-btn
          class="homeEvents_title_btn"
          :color="isSearchTriggered ? 'lightBlue' : ''"
          x-small
          @click="showSearch = !showSearch"
        >
          <v-icon
            v-if="isSearchTriggered"
            small
            @click.stop="refreshSelectedToggles"
          >
            mdi-close
          </v-icon>
          <span
            >絞りこみ{{
              isSearchTriggered
                ? '：' + (events ? events.length : 0) + '件'
                : ''
            }}</span
          ><span
            color="primary"
            class="icon icon-chevron-down"
            :class="{ 'homeEvents_title_icon-open': showSearch }"
          />
        </v-btn>
      </div>
      <HomeEventsSearch
        :has-date.sync="hasDate"
        :dates.sync="dates"
        :camera-ids.sync="cameraIds"
        :search-keys.sync="searchKeys"
        :selected-cams.sync="selectedCams"
        :selected-search-keys.sync="selectedSearchKeys"
        class="homeEvents_search"
        :class="{ 'homeEvents_search-open': showSearch }"
        @search="handleSearch(true)"
      />
      <div class="homeEvents_events_container scrollParent">
        <app-skeleton
          :loading="skeletonLoading"
          :nodata="skeletonNoData"
          nodata-text="イベントが見つかりません"
          skeleton="homeEvents"
        />
        <virtual-list
          ref="vlist"
          :class="{ scrollHidden: !firstEventBatchLoaded }"
          class="homeEvents_events scrollChild"
          :data-key="'eventId'"
          :data-sources="dataItems"
          :data-component="itemComponent"
          :extra-props="{ hideLink: false, hideDuration: true }"
          :estimate-size="40"
          @tobottom="onScrollToBottom"
        >
          <div slot="footer" class="homeEvents_progress">
            <v-progress-circular
              v-if="isProgressVisible"
              color="#cccccc"
              :size="30"
              :width="2"
              indeterminate
            ></v-progress-circular>
          </div>
        </virtual-list>
      </div>
    </v-sheet>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import EventService from '@/services/event.service';
import EventItem from '@/components/EventItem.vue';
import HomeEventsSearch from './HomeEventsSearch.vue';
import VirtualList from 'vue-virtual-scroll-list';
import EventBus from '@/utils/eventBus';
import { abortKeys, CallerKeys, EventBusEvents } from '@/globals/enums';
import dayjs from 'dayjs';
import { TIME_RANGE_DURATION } from '@/globals/constants';
import { sliceArrayByPage } from '@/utils/helpers';
import { AppAbortController } from '@/utils/abortController';
import { registerHttpRequest } from '@/services/api/http.service';

export default {
  name: 'HomeEvents',
  components: {
    EventItem,
    HomeEventsSearch,
    VirtualList,
  },
  props: {
    homeTimestamp: {
      type: Number,
      required: true,
    },
    homeTimerange: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      itemComponent: EventItem,
      eventService: new EventService(),
      page: 1,
      loading: false,
      hasMore: true,
      dataItems: [],
      dataItemsIsLoading: false,
      alertEvents: [],
      hasDate: false,
      cameraIds: [],
      searchKeys: [],
      showSearch: false,
      selectedCams: {},
      selectedSearchKeys: {},
      isSearchTriggered: false,
      currentOffset: 0,
      initialUpperLimit: 1000,
      subsequentUpperLimit: 50,
      imageErrorCounter: true,
      startDateParams: null,
      endDateParams: null,
      componentAbortController: new AbortController(),
      startDate: null,
      endDate: null,
      searchRunId: null,
      baseLoading: false,
      pendingBaseRun: false,
      baseLoadingDepth: 0,
      loadingPhase: 'idle', // 'idle' | 'initial' | 'paginating' | 'search'
      inFlightCount: 0,
    };
  },
  computed: {
    ...mapGetters('common', {
      currentGroupCamList: 'currentGroupCamList',
      maxDays: 'maxRetentionPeriodDays',
      minRetentionDate: 'minRetentionDate',
    }),
    ...mapState({
      cameraList: (state) => state.common.cameraList,
      area: (state) => state.common.area,
      events: (state) => state.event.searchEvents,
      areaCoordinates: (state) => state.common.areaCoordinates,
      timeRange: (state) => state.home.timeRange,
      homeSeekStart: (state) => state.home.homeSeekStart,
      firstEventBatchLoaded: (state) => state.event.firstEventBatchLoaded,
      currentSearchRunIdStore: (state) => state.event.currentSearchRunId,
    }),
    routeId() {
      return this.$route.query.event;
    },
    dates: {
      get() {
        return [this.startDate, this.endDate];
      },
      set(val) {
        this.startDate = val[0];
        this.endDate = val[1];
      },
    },
    areaVal: {
      get() {
        return this.area;
      },
      set(val) {
        this.changeArea(val);
      },
    },
    isProgressVisible() {
      return this.hasMore || this.isPartialDataLoaded;
    },
    isPartialDataLoaded() {
      // only consider partial when some items are rendered and events has more
      const eventsLength = this.events ? this.events.length : 0;
      return (
        !this.hasMore &&
        this.dataItems.length > 0 &&
        this.dataItems.length !== eventsLength
      );
    },
    skeletonLoading() {
      // show loader when a base/init load is in progress (including a pending scheduled run),
      // any request is loading, or camera list not ready
      return (
        this.pendingBaseRun ||
        this.baseLoadingDepth > 0 ||
        this.loading ||
        this.cameraList.length === 0
      );
    },
    skeletonNoData() {
      // show No Data only when nothing is loading and there are no items
      const nothingLoaded =
        (!this.events || this.events.length === 0) &&
        this.dataItems.length === 0;
      return !this.baseLoading && !this.loading && nothingLoaded;
    },
  },
  watch: {
    firstEventBatchLoaded(val) {
      val && (this.loading = false);
    },
    minRetentionDate: {
      handler(val, oldVal) {
        if (val && !oldVal) {
          const [start, end] = this.getInitialDate();
          this.startDate = start;
          this.endDate = end;
        }
      },
      immediate: true,
    },
    events(val) {
      // perform the initial page slice when events arrive for the current run
      if (
        Array.isArray(val) &&
        val.length > 0 &&
        this.dataItems.length === 0 &&
        this.page === 1 &&
        (!this.searchRunId ||
          !this.currentSearchRunIdStore ||
          this.searchRunId === this.currentSearchRunIdStore)
      ) {
        const newPage = sliceArrayByPage(
          val,
          this.subsequentUpperLimit,
          this.page
        );
        this.dataItems = newPage;
        this.page += 1;
      }
    },
  },
  created() {
    this.setHomeEventMounted(true);
    this.setFirstEventBatchLoaded(false);
  },
  mounted() {
    this.init();
    EventBus.on(EventBusEvents.CallHomeEvents, this.handleHomeEventBus);
  },
  beforeDestroy() {
    this.componentAbortController.abort();
    this.setHomeEventMounted(false);
    this.abortFetchingImages(
      'Abort fetching thumbnails before component destroy'
    );
    EventBus.off(EventBusEvents.CallHomeEvents, this.handleHomeEventBus);
    this.resetSearch();
    this.clearSeekbarEvents();
  },
  methods: {
    async init() {
      this.initEventList();
    },
    // loading lifecycle helpers
    beginRequest(phase) {
      if (phase) {
        this.loadingPhase = phase;
      }
      this.inFlightCount += 1;
      // preserve original UI semantics: only initial/search affect skeleton/loading
      if (phase === 'initial' || phase === 'search') {
        this.baseLoadingDepth += 1;
        this.baseLoading = true;
        this.loading = true;
      }
    },
    endRequest() {
      this.inFlightCount = Math.max(0, this.inFlightCount - 1);
      if (this.loadingPhase === 'initial' || this.loadingPhase === 'search') {
        this.baseLoadingDepth = Math.max(0, this.baseLoadingDepth - 1);
        if (this.baseLoadingDepth === 0) {
          this.baseLoading = false;
          this.loading = false;
        }
      }
      if (this.inFlightCount === 0) {
        this.loadingPhase = 'idle';
        if (this.baseLoadingDepth === 0) {
          this.pendingBaseRun = false;
        }
      }
    },
    // pagination helpers
    resetPagination() {
      const alreadyReset =
        this.page === 1 &&
        this.hasMore === true &&
        this.dataItems.length === 0 &&
        this.currentOffset === 0 &&
        this.dataItemsIsLoading === false;
      if (alreadyReset) {
        return;
      }
      this.page = 1;
      this.hasMore = true;
      this.dataItems = [];
      this.dataItemsIsLoading = false;
      this.currentOffset = 0;
      this.setFirstEventBatchLoaded(false);
    },
    buildParams({ offset, upperLimit }) {
      const params = {
        cameraIds:
          this.cameraIds.length > 0
            ? this.cameraIds
            : this.currentGroupCamList.map((cam) => cam.id),
        searchKeys: [],
        offset,
        upperLimit,
      };
      // home event list filter vs seeker-driven
      const hasExplicitDate =
        this.hasDate && Array.isArray(this.dates) && this.dates.length === 2;
      if (this.isSearchTriggered) {
        if (hasExplicitDate) {
          params.startDate = this.dates[0];
          params.endDate = this.dates[1];
        }
      } else {
        if (hasExplicitDate) {
          params.startDate = this.dates[0];
          params.endDate = this.dates[1];
        }
        if (this.startDateParams) {
          params.startDate = this.startDateParams;
        }
        if (this.endDateParams) {
          params.endDate = this.endDateParams;
        }
      }
      this.searchKeys.forEach((searchKey) => {
        if (searchKey && searchKey.value) {
          params.searchKeys.push({ key: 'content', value: searchKey.value });
        }
      });
      if (this.area) {
        params.searchKeys.push({
          key: '$area',
          value: this.areaCoordinates.join(),
        });
      }
      if (this.searchRunId) {
        params.__searchRunId = this.searchRunId;
      }
      return params;
    },
    getInitialDate({ minRetentionDate, homeTimestamp, homeTimerange } = {}) {
      const minDate = minRetentionDate || this.minRetentionDate;
      const startTimestamp = homeTimestamp || this.homeTimestamp;
      const timeRange = homeTimerange || this.homeTimerange;

      const seekbarEndDate = dayjs(startTimestamp).add(
        timeRange * TIME_RANGE_DURATION,
        'minutes'
      );

      let startDate;
      let endDate;

      startDate = dayjs(minDate).format('YYYYMMDD000000');
      endDate = seekbarEndDate;

      endDate = dayjs(Math.min(endDate.valueOf(), dayjs().valueOf())).format(
        'YYYYMMDDHHmmss'
      );

      return [startDate, endDate];
    },
    async initEventList() {
      // local reset for fresh run
      this.pendingBaseRun = false;
      this.resetPagination();

      this.hasMore = await this.getEventList(true);

      if (this.hasMore) {
        this.currentOffset +=
          this.currentOffset > 0
            ? this.subsequentUpperLimit
            : this.initialUpperLimit;
      }
    },
    handleHomeEventBus(action) {
      switch (action.type) {
        case 'fetch-events':
          this.fetchEventsOnTimestamChange(action.payload);
          break;

        default:
          console.warn(
            `HomeEvents: Unknown action type received from EventBus: ${action.type}`
          );
          break;
      }
    },
    fetchEventsOnTimestamChange({
      timestamp,
      endtime,
      timeRange,
      minRetentionDate,
      shouldReset,
      caller,
    } = {}) {
      if (
        [
          CallerKeys.HomeVideo_handleHomeGroupChanged,
          CallerKeys.HomeVideo_watchMaxRetentionPeriodDays,
        ].includes(caller)
      ) {
        this.clearSeekbarEvents();
      }
      if (shouldReset) {
        this.hasDate = false;
        this.startDateParams = null;
        this.endDateParams = null;
        this.dates = this.getInitialDate();
      } else {
        this.setStartEndDateParams({
          timestamp,
          endtime,
          timeRange,
          minRetentionDate: minRetentionDate || this.minRetentionDate,
          caller,
        });
      }
      this.$nextTick(() => {
        this.abortAndRefresh();
      });
    },
    abortFetchingImages(reason = 'Abort fetching thumbnails') {
      AppAbortController.abortStartsWith(
        abortKeys.fetchSearchEventImages,
        reason
      );
    },
    async onScrollToBottom() {
      if (
        this.dataItemsIsLoading ||
        (!this.hasMore && this.events.length === this.dataItems.length)
      ) {
        return;
      }
      await this.fetchNextPage();
    },
    async fetchNextPage() {
      this.dataItemsIsLoading = true;
      if (
        this.initialUpperLimit === this.dataItems.length ||
        this.dataItems.length > this.initialUpperLimit
      ) {
        const result = await this.getEventList(false);
        this.hasMore = result;
        if (this.hasMore) {
          this.currentOffset +=
            this.currentOffset > 0
              ? this.subsequentUpperLimit
              : this.initialUpperLimit;
        }
      } else {
        await this.delayPromise(500);
      }
      this.dataItemsIsLoading = false;
      const newPage = sliceArrayByPage(
        this.events,
        this.subsequentUpperLimit,
        this.page
      );
      this.page += 1;
      this.dataItems = this.dataItems.concat(newPage);
      // new items won't be loaded in the HTML DOM if no scroll
      setTimeout(() => {
        const newOffset = this.$refs.vlist.getOffset() + 3;
        this.$refs.vlist.scrollToOffset(newOffset);
      }, 500);
    },
    handleSearch(isSearchTriggered) {
      this.isSearchTriggered = isSearchTriggered;
      // hard reset local pagination state before any computed runs
      this.pendingBaseRun = true;
      this.baseLoading = true;
      this.loading = true;
      this.resetPagination();
      this.abortFetchingImages('Abort fetching thumbnails on handle search');

      // start a new search run id to guard against stale commits
      this.searchRunId = `${Date.now()}-${Math.random()}`;
      this.$store.dispatch('event/setCurrentSearchRunId', this.searchRunId);
      this.resetSearchEvents();
    },
    refreshSelectedToggles() {
      this.hasDate = false;
      this.cameraIds = [];
      this.searchKeys = [];
      this.selectedCams = {};
      this.selectedSearchKeys = {};
      this.areaVal = false;
      this.setFirstEventBatchLoaded(false);

      this.handleSearch(false);
    },
    async getEventList(showLoading) {
      // mark lifecycle
      this.beginRequest(showLoading ? 'initial' : 'paginating');
      const params = this.buildParams({
        offset: this.currentOffset,
        upperLimit:
          this.currentOffset > 0
            ? this.subsequentUpperLimit
            : this.initialUpperLimit,
      });
      try {
        // abort any in-flight HomeEvents list request when starting a new base search (offset === 0)
        registerHttpRequest(
          'event.getEventList.homeEvents',
          params,
          this.currentOffset > 0
        );

        const { hasMore, list } = await this.fetchSearchEvents(params);

        if (this.componentAbortController.signal.aborted) {
          return false;
        }
        // fetch thumbnails; swallow abort errors to avoid unhandled promise rejection
        try {
          if (Array.isArray(list) && list.length > 0) {
            this.getEventThumbnails(list, params);
          }
        } catch (e) {
          // eslint-disable-next-line no-console
        }
        // ensure first page is rendered immediately using API response to avoid race with Vuex commit
        if (
          params.offset === 0 &&
          Array.isArray(list) &&
          list.length > 0 &&
          this.dataItems.length === 0 &&
          this.page === 1
        ) {
          const initialSlice = list.slice(0, this.subsequentUpperLimit);
          this.dataItems = initialSlice;
          this.page += 1;
        }
        return hasMore;
      } catch (err) {
        return false;
      } finally {
        this.endRequest();
      }
    },
    getEventThumbnails(list, params) {
      if (!params.offset) {
        AppAbortController.abortStartsWith(
          abortKeys.fetchSearchEventImages,
          'Offset starts from zero on calling fetch search event images'
        );
      }
      const abortKey = `${abortKeys.fetchSearchEventImages}:${params.offset}`;
      AppAbortController.newController(abortKey);
      this.fetchEventThumbnails({
        list,
        offset: params.offset,
        abortKey,
      }).catch((error) => {
        // eslint-disable-next-line no-console
        console.error('[Error] fetchEventThumbnails failed:', error);
      });
    },
    goToEvent(id) {
      if (+id !== +this.routeId) {
        this.$router.push({ name: 'home', params: { id } });
      }
    },
    resetSearchEvents() {
      this.resetSearch();
      this.$refs?.vlist && this.$refs.vlist.reset();
      this.$nextTick(() => {
        // mark that a base run will start on nextTick, so skeleton remains visible
        this.pendingBaseRun = true;
        this.initEventList();
      });
    },
    setStartEndDateParams({ timestamp, endtime, timeRange, minRetentionDate }) {
      this.startDateParams = dayjs(minRetentionDate).format('YYYYMMDDHHmmss');

      const seekbarEndDate = dayjs(timestamp).add(
        timeRange * TIME_RANGE_DURATION,
        'minutes'
      );
      const calculatedEndDate =
        endtime || Math.min(seekbarEndDate.valueOf(), dayjs().valueOf());

      this.endDateParams = dayjs(calculatedEndDate).format('YYYYMMDDHHmmss');

      // Synchronize calendar of home event search
      this.dates = [this.startDateParams, this.endDateParams];
      [this.startDate, this.endDate] = this.dates;
    },
    abortAndRefresh() {
      this.refreshSelectedToggles();
    },
    ...mapActions('event', {
      clearSeekbarEvents: 'clearSeekbarEvents',
      fetchSearchEvents: 'fetchSearchEvents',
      fetchEventThumbnails: 'fetchSearchEventImages',
      resetSearch: 'resetSearchEvents',
      setHomeEventMounted: 'setHomeEventMounted',
      setFirstEventBatchLoaded: 'setFirstEventBatchLoaded',
    }),
    ...mapActions('common', ['changeArea']),
  },
};
</script>

<style lang="scss">
.homeEvents {
  &_progress {
    width: 100%;
    height: 50px;
    background-color: #fff;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  &_notification {
    &.v-sheet.theme--light {
      .homeEvents & {
        flex-grow: 0;
        margin-bottom: 30px;
        color: #fff;
        background-color: var(--primary-color);
        padding-bottom: 16px;
        box-shadow: 0 3px 12px 0 rgba(0, 45, 170, 0.19);
      }
    }
    &_supTitle {
      font-size: 14px;
      font-weight: 600;
      line-height: 17px;
      margin-bottom: 7px;
    }
    &_title {
      font-size: 16px;
      font-weight: 600;
      line-height: 21px;
      margin-bottom: 2px;
    }
    .eventItem {
      border-top: 0;
      padding-left: 0;
      padding-right: 0;
    }
    .eventItem_time {
      color: #fff;
    }
    .eventItem_date {
      font-weight: 400;
    }
    .eventItem_hour {
      font-weight: 400;
    }
    .eventItem_chevron {
      display: none;
    }
  }
  &_main {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    &.v-sheet {
      padding-right: 22px;
    }
  }
  &.v-sheet {
    padding-right: 22px;
  }
  &_title {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 17px;
    padding-right: 8px;
    &_text {
      font-size: 14px;
      font-weight: 600;
      color: var(--text-color);
    }
    &_btn {
      .icon {
        margin-left: 8px;
        position: relative;
        margin-right: -5px;
        position: relative;
      }
    }
    &_icon {
      &-open {
        transform: rotate(180deg);
      }
      &-refresh {
        margin-right: 5px;
      }
    }
  }
  &_search {
    max-height: 0;
    margin: 0 -22px 0 -30px;
    padding: 0 30px;
    transition: max-height 0.2s cubic-bezier(0.25, 0.8, 0.5, 1);
    overflow-x: hidden;
    overflow-y: auto;
    &-open {
      max-height: 390px;
    }
  }
  &_events {
    padding-right: 8px;
    &_container {
      &:before {
        content: '';
        position: absolute;
        top: 0;
        right: 8px;
        left: 0;
        height: 1px;
        background: #ccc;
        z-index: 3;
      }
      &:after {
        content: '';
        position: absolute;
        background-color: #d1d2d8;
        bottom: 0;
        left: 0;
        right: 8px;
        height: 1px;
      }
    }
  }
}
</style>
